import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import ArrowDown from "../../../assets/icons/ArrowDown";
import ArrowUp from "../../../assets/icons/ArrowUp";
import Wrapper from "../../helpers/Wrapper";
import Button from "../Button";
import NoDataAvailableLoader from "../loaders/NoDataAvailable";
import SomethingWentWrong from "../loaders/SomethingWentWrong";
import SectionDefinationsBadge from "../SectionDefinationsBadge";
import { useSelector } from "react-redux";
import { FiSearch, FiPlus, FiMinus, FiSliders } from "react-icons/fi";
import "../../../styles/NestedSummaryTable.scss";
import ExportDropdown from "../dropdown/ExportDropdown";
import IconDeGrowth from "../../../assets/icons/DegrowthIcon";
import IconGrowth from "../../../assets/icons/GrowthIcon";

import Loader from "../loaders/Loader";

// Searchable fields and group by fields
const SEARCHABLE_HEADER_FIELDS = [
  "time_period", "segment", "store_type", "manufacture", "brand", "variant", "pack_size", "sku"
];

const GROUP_BY_FIELDS = [
  "segment", "store_type", "manufacture", "brand", "variant", "pack_size", "sku"
];

const TABLE_HEADERS = [
  { name: "Sales Value", field: "sales_value" },
  { name: "Sales Volume", field: "sales_volume" },
  { name: "Price Per Volume", field: "ppl" },
  { name: "Weighted Distribution Points (TDP)", field: "tdp" },
  { name: "Numeric Distribution Selling", field: "numdtotal" },
  { name: "Market Share By Sales Value", field: "market_share_by_sales_value" },
  { name: "Market Share By Sales Volume", field: "market_share_by_sales_volume" },
  { name: "Promotional Sales Value %", field: "promotional_sales_value" },
  { name: "Promotional Sales Volume %", field: "promotional_sales_volume" },
  { name: "Sales Value Growth", field: "sales_value_growth" },
  { name: "Sales Volume Growth", field: "sales_volume_growth" },
  { name: "Price Per Volume Growth", field: "ppl_growth" },
  { name: "Weighted Distribution Points (TDP) Growth", field: "tdp_growth" },
  { name: "Numeric Distribution Selling Growth", field: "numdtotal_growth" },
  { name: "Market Share By Sales Value Growth", field: "market_share_by_sales_value_growth" },
  { name: "Market Share By Sales Volume Growth", field: "market_share_by_sales_volume_growth" },
  { name: "Promotional Sales Value % Growth", field: "promotional_sales_value_growth" },
  { name: "Promotional Sales Volume % Growth", field: "promotional_sales_volume_growth" },
  { name: "Sales Value per NumDTotal (No.of Store %)", field: "sales_value_per_numdtotal" },
  { name: "NumDTotal (No. of Store %)/Sales Volume", field: "numdtotal_no_of_store_per_sales_volume" },
  { name: "NumDTotal(No. of Store %)", field: "numdtotal_no_of_store" },
  { name: "Sales Volume per SKU", field: "sales_volume_per_sku" },
  { name: "No. of SKU per brand", field: "no_of_sku_per_brand" },
  { name: "No. of Brand per NumDTotal (No. of Store %)", field: "no_of_brand_per_store" },
  { name: "Sales Alert Status", field: "sales_alert_status" },
  { name: "Sales Alert Reason", field: "sales_alert_reason" },
  { name: "Time Period", field: "time_period" }
];

// Utility function to render cell value or '-'
const renderCellValue = (value) => {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
    return "-";
  }
  // Check if value is a number or a numeric string
  const numValue = typeof value === "number" ? value : parseFloat(value);
  if (!isNaN(numValue)) {
    const decimalPart = numValue.toString().split(".")[1];
    if (decimalPart && decimalPart.length > 4) {
      return numValue.toFixed(4);
    }
  }
  return value;
};

const stringHeaderTitles = [
  "Hashtag", "Channel Name", "Landing Page", "Source", "Category", "Categories", "Event Action", "Product Name", "City Name", "Source Type", "Name", "Platform", "Objective", "Campaign Name", "Zone", "State", "Creative Name", "Full Name", "Profile", "Age", "Gender", "Device", "Link", "Country", "City", "Campaign Bucket", "Start Date", "End Date", "Brands", "Line Item Name", "Markets", "Brand Name", "Region", "Medium", "Publishing Medium", "Ad Name", "Geography", "Week/Month", "VG Group", "Date", "Keyword", "Ad Group Name", "Ad ID", "Search Term", "Landing Page", "Campaign Group Name", "Ad group Name", "Keyword Name", "Post ID", "Creative ID", "Creative", "Line Item", "Bucket Name", "Ad Set Name", "Event Name", "Channel", "Event Type", "Destination URL"
];
const HeaderTitleAlignmentSetter = (headerTitle) => stringHeaderTitles.includes(headerTitle);

const NestedSummaryTable = ({
  tableTitle,
  tableHeader,
  tableData,
  tableLength,
  tableDropdown,
  searchPlaceHolder,
  searchSingleColumnNumberRow,
  searchSingleColumnNumber,
  hrefTitle,
  tableExportDropdown,
  tableID,
  tablePlatform,
  checkbox,
  onCheckboxClickHandler,
  isDynamicFlagVisible,
  showGrowthDegrowth,
  showTotal,
  isCreatableDropdown,
  tableTitleClass,
  expandedNestedRows,
  setExpandedNestedRows,
  handleExpandNestedRow,
  nestedData,
  loadingNested,
}) => {
  // ---------- State & Hooks ----------
  // Add pagination state here
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(tableLength || 10);
  const [checkedItems, setCheckedItems] = useState([]);
  const [formattedTableHeaders, setFormattedTableHeaders] = useState([]);
  const [showColumnFilter, setShowColumnFilter] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(tableHeader.map((h) => h.field));
  const [columnSearch, setColumnSearch] = useState("");
  const [tempColumns, setTempColumns] = useState(visibleColumns);
  const [filterTableHeaders, setFilterTableHeaders] = useState(tableHeader);
  const columnFilterRef = useRef(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [headerSearch, setHeaderSearch] = useState({});
  const selectedDates = useSelector((state) => state.date);

  // --- CLIENT-SIDE SEARCH & SORT ---
  // Persist clientSearch input values after expand, pagination, and sort
  const [clientSort, setClientSort] = useState({ order_by: null, order_type: 'asc' });
  const [clientSearch, setClientSearch] = useState(() => {
    // Try to restore from localStorage if available
    try {
      const saved = window.localStorage.getItem('nestedTableClientSearch');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Save clientSearch to localStorage on change
  useEffect(() => {
    try {
      window.localStorage.setItem('nestedTableClientSearch', JSON.stringify(clientSearch));
    } catch {}
  }, [clientSearch]);
  

  // useEffect(() => {
  //   // On filter changes (tableHeader or tableData), reset clientSearch and localStorage
  //   setClientSearch({});
  //   try {
  //     window.localStorage.removeItem('nestedTableClientSearch');
  //   } catch {}
  // }, [tableHeader, tableData]);

  // --- Pagination Logic (now inside this component) ---
  // const currentPage = tableConfigs?.page_num || 1;
  // const pageSize = tableConfigs?.page_size || tableLength || 10;

  // Filter and sort data before paginating
  const clientFilteredSortedData = useMemo(() => {
    // Use both [field] and [field + '_input'] for filtering
    let filtered = tableData.filter(row =>
      formattedTableHeaders.every(header => {
        const field = header.field;
        // Use the input value if present, else the committed value
        const searchValue = clientSearch[field];
        if (!SEARCHABLE_HEADER_FIELDS.includes(field) || !searchValue) return true;
        const cellValue = (row[field] || "").toString().toLowerCase();
        return cellValue.includes(searchValue.trim().toLowerCase());
      })
    );
    if (clientSort.order_by) {
      const orderBy = clientSort.order_by;
      const orderType = clientSort.order_type === "desc" ? -1 : 1;
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1 * orderType;
        if (bValue == null) return -1 * orderType;
        const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
        const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
        const aIsNum = !isNaN(aNum) && aValue !== null && aValue !== '' && isFinite(aNum);
        const bIsNum = !isNaN(bNum) && bValue !== null && bValue !== '' && isFinite(bNum);
        if (aIsNum && bIsNum) {
          if (aNum < bNum) return -1 * orderType;
          if (aNum > bNum) return 1 * orderType;
          return 0;
        }
        aValue = aValue === undefined || aValue === null ? '' : aValue.toString();
        bValue = bValue === undefined || bValue === null ? '' : bValue.toString();
        return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' }) * orderType;
      });
    }
    return filtered;
  }, [tableData, formattedTableHeaders, clientSearch, clientSort]);

  // --- Paginate the filtered/sorted data ---
  const totalItems = clientFilteredSortedData.length;
  const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedData = clientFilteredSortedData.slice(startIdx, endIdx);
  // console.group()
  // console.log('totalItems:', totalItems);
  // console.log('totalPages:', totalPages);
  // console.log('startIdx:', startIdx);
  // console.log('endIdx:', endIdx);
  // console.log('paginatedData:', paginatedData);
  // console.groupEnd();

  // Effects
  useEffect(() => {
    function handleClickOutside(event) {
      if (columnFilterRef.current && !columnFilterRef.current.contains(event.target)) {
        setShowColumnFilter(false);
      }
    }
    if (showColumnFilter) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnFilter]);
  useEffect(() => {
    setVisibleColumns(tableHeader.map((h) => h.field));
    setTempColumns(tableHeader.map((h) => h.field));
    setFilterTableHeaders(tableHeader);
  }, [tableHeader]);
  useEffect(() => {
    // Put id fields at end if any
    const IDFields = ["id"];
    var hasIDs = tableHeader.some((x) => IDFields.includes(x.field));
    if (hasIDs) {
      const dataArray = [...tableHeader];
      const indexOfId = dataArray.findIndex((item) => IDFields.includes(item.field));
      if (indexOfId !== -1) {
        const idObject = dataArray.splice(indexOfId, 1)[0];
        dataArray.push(idObject);
      }
      setFormattedTableHeaders(dataArray);
    } else {
      setFormattedTableHeaders(tableHeader);
    }
  }, [tableHeader]);
  useEffect(() => {
    // Remove clearSelectedItems usage
    // If you want to clear checked items, use a local function or prop
    // Example: setCheckedItems([]); if needed
  }, []);

  // Column Picker Logic
  const openColumnFilter = () => {
    setTempColumns(visibleColumns);
    setColumnSearch("");
    setFilterTableHeaders(tableHeader);
    setShowColumnFilter(true);
  };

  // --- NESTED PAYLOAD BUILDER: Always include top-level time_period in filter_select ---
  const getPayloadForLevel = (level, parentRow, parentKey) => {
    const selectedValues = {};
    let timePeriod = parentRow.time_period;
    // Remove topLevelRows logic
    if (timePeriod) {
      selectedValues.time_period = timePeriod;
    }
    for (let i = 0; i < level - 1; i++) {
      const field = GROUP_BY_FIELDS[i];
      if (parentRow[field]) {
        selectedValues[field] = parentRow[field];
      }
    }
    return {
      global_filter: {
        date_filter: {
          mat_ty_start_date: selectedDates.startDate,
          mat_ty_end_date: selectedDates.endDate,
          mat_ly_start_date: selectedDates.prevStartDate,
          mat_ly_end_date: selectedDates.prevEndDate,
          mat_2y_start_date: selectedDates.secondPrevStartDate,
          mat_2y_end_date: selectedDates.secondPrevEndDate,
        }
      },
      section_filter: {
        group_by_clause: GROUP_BY_FIELDS.slice(0, level),
        filter_select: [selectedValues],
        time_period: timePeriod
      }
    };
  };

  // Table HEADER + BODY
  let DataTable;
  try {
    DataTable = (
      <Wrapper>
        <div className="nested-summary-table-wrapper">
          <table id={tableID} className="table table_striped table_bordered nested-summary-table">
            <thead>
  <tr>
    {formattedTableHeaders
      .filter(header => visibleColumns.includes(header.field) && header.is_display)
      .map((header, colIdx) => {
        // Check if tableData contains this field in any row
        const fieldExists = tableData.some(row => Object.prototype.hasOwnProperty.call(row, header.field));
        return (
          <th
            key={header.name}
            className={`${header.name === "Brand" ? "brand_align" : ""} ${colIdx === 0 ? "fixed-col" : ""}`}
            style={{ position: "relative" }}
          >
            <div className="th_wrapper" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div
                className="th_nested_title"
                style={{
                  cursor: fieldExists ? "pointer" : "not-allowed",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  opacity: fieldExists ? 1 : 0.5
                }}
                onClick={() => {
                  if (!fieldExists) return;
                  setClientSort(prev => ({
                    order_by: header.field,
                    order_type:
                      prev.order_by === header.field && prev.order_type === "asc"
                        ? "desc"
                        : "asc"
                  }));
                }}
              >
                {header.name}
                <span className="icon_wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, height: 20, marginLeft: 4 }}>
                  <ArrowUp
                    style={{
                      fontSize: 14,
                      lineHeight: 1,
                      opacity: clientSort.order_by === header.field && clientSort.order_type === "asc" && fieldExists ? 1 : 0.5
                    }}
                    className={clientSort.order_by === header.field && clientSort.order_type === "asc" && fieldExists ? "icon active" : "icon"}
                  />
                  <ArrowDown
                    style={{
                      fontSize: 14,
                      lineHeight: 1,
                      opacity: clientSort.order_by === header.field && clientSort.order_type === "desc" && fieldExists ? 1 : 0.5
                    }}
                    className={clientSort.order_by === header.field && clientSort.order_type === "desc" && fieldExists ? "icon active" : "icon"}
                  />
                </span>
              </div>
              {SEARCHABLE_HEADER_FIELDS.includes(header.field) && (
                <div className="datatable-column-search-custom">
                  <input
                    type="text"
                    value={clientSearch[header.field + '_input'] || ''}
                    onChange={e => {
                      if (!fieldExists) return;
                      setClientSearch(search => ({
                        ...search,
                        [header.field + '_input']: e.target.value
                      }));
                    }}
                    className="datatable-column-search-custom-input"
                    spellCheck={false}
                    disabled={!fieldExists}
                    style={{  cursor: !fieldExists ? "not-allowed" : undefined }}
                    onKeyDown={e => {
                      if (!fieldExists) return;
                      if (e.key === 'Enter') {
                        setClientSearch(search => ({
                          ...search,
                          [header.field]: search[header.field + '_input'] || '',
                          [header.field + '_input']: search[header.field + '_input'] || '',
                        }));
                        setCurrentPage(1); // Reset to first page on search
                      }
                    }}
                  />
                  <span
                    className="datatable-search-icon-custom"
                    style={{ cursor: fieldExists ? 'pointer' : 'not-allowed', opacity: fieldExists ? 1 : 0.5 }}
                    onClick={() => {
                      if (!fieldExists) return;
                      const inputValue = clientSearch[header.field + '_input'] || '';
                      setClientSearch(search => ({
                        ...search,
                        [header.field]: inputValue,
                        [header.field + '_input']: inputValue,
                      }));
                      setCurrentPage(1); // Reset to first page on search
                    }}
                  >
                    <FiSearch size={22} />
                  </span>
                </div>
              )}
            </div>
          </th>
        );
      })}
  </tr>
</thead>
            <tbody>
  {paginatedData.length === 0 ? (
    <tr>
      <td colSpan={formattedTableHeaders.length}>
        <NoDataAvailableLoader />
      </td>
    </tr>
  ) : (
    paginatedData.map((row, idx) => {
      const canExpand = row.__level < 7; // Or your maxLevel value
      return (
        <tr
          key={row.__parentKey}
          className={`nested-row-level-${row.__level}`}
          style={{
            background: row.__level > 1 ? "#FFF" : undefined,
            fontWeight: 400,
            color: "#223046"
          }}
        >
          {formattedTableHeaders
            .filter(header => visibleColumns.includes(header.field) && header.is_display)
            .map((header, colIdx) => {
              // Check for Alert Cause column
              if (header.name === "Alert Cause") {
                const cellValue = row[header.field];
                let alertClass = "";
                let displayValue = "-";
                      if (cellValue === "positive") {
                        alertClass = "nested-summary-table-alert positive";
                        displayValue = renderCellValue("sales value");
                      } else if (cellValue === "negative") {
                        alertClass = "nested-summary-table-alert negative";
                        displayValue = renderCellValue("sales value");
                      }
                      return (
                        <td key={colIdx}>
                          <span className={alertClass}>{displayValue}</span>
                        </td>
                      );
    }
              return (
                <td
                  key={colIdx}
                  className={colIdx === 0 ? "fixed-col with-plus-icon" : ""}
                  style={{
                    display: colIdx === 0 ? "flex" : undefined,
                    alignItems: colIdx === 0 ? "center" : undefined,
                    gap: colIdx === 0 ? "10px" : undefined,
                    fontWeight: 400,
                    paddingLeft: colIdx === 0 ? `${16 + row.__level * 20}px` : undefined,
                    background: row.__level > 1 ? "#FFF" : undefined,
                    color: "#223046"
                  }}
                >
                  {colIdx === 0 && canExpand && (
                    <span
                      className="datatable__plus-icon"
                      style={{
                        cursor: "pointer",
                        color: expandedNestedRows[row.__parentKey] ? "#223046" : "#999",
                        fontWeight: expandedNestedRows[row.__parentKey] ? 700 : 400
                      }}
                      onClick={() => handleExpandNestedRow(row.__parentKey, row.__level, row)}
                    >
                      {expandedNestedRows[row.__parentKey] ? <FiMinus size={18} /> : <FiPlus size={18} />}
                    </span>
                  )}
                  {colIdx === 0 && !canExpand && (
                    <span style={{ width: 18, display: "inline-block" }}></span>
                  )}
                  <span>
                    {/* If header has growth_field, show icon based on growth_field value */}
                    {header.growth_field && typeof row[header.growth_field] !== 'undefined' && row[header.growth_field] !== null && row[header.growth_field] !== 'N/A'? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {header.is_display ? renderCellValue(row[header.field]) : row[header.field]}
                        {parseFloat(row[header.growth_field]) < 0 ? (
                          <span title={row[header.growth_field]} className="icon_wrapper">
                            <IconDeGrowth style={{ verticalAlign: 'middle' }} />
                          </span>
                        ) : (
                          <span title={row[header.growth_field]} className="icon_wrapper">
                            <IconGrowth style={{ verticalAlign: 'middle' }} />
                          </span>
                        )}
                        
                      </span>
                    ) : (
                      renderCellValue(row[header.field])
                    )}
                  </span>
                </td>
              );
            })}
        </tr>
      );
    })
  )}
</tbody>
          </table>
        </div>
      </Wrapper>
    );
  } catch (error) {
    DataTable = (
      <Wrapper>
        <SomethingWentWrong />
      </Wrapper>
    );
  }

  // ========== RETURN ==========
  return (
    <div className="grid grid_cols_4 fullpage_section_card section_card">
      <div className="grid col_span_4">
        <div className="section_card_body">
          <Wrapper>
            <div className="section_card_header">
              <div className="section_info">
                <h2 className={`section_card_title ${tableTitleClass ? tableTitleClass : ""}`}>
                  {tableTitle}
                </h2>
                {/* Remove SectionDefinationsBadge logic unless you define those props */}
              </div>
              <div className="filters_wrapper">
                <div data-html2canvas-ignore={true} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div className="filter-btn-dropdown-wrapper">
                    <button type="button" className="filter-btn" onClick={openColumnFilter} aria-label="Filter">
                      <FiSliders size={22} />
                    </button>
                    {showColumnFilter && (
                      <div className="column-filter-dropdown" ref={columnFilterRef}>
                        <div className="column-filter-header">
                          <span>Show/Hide Column</span>
                          <button className="close-btn" onClick={() => setShowColumnFilter(false)}>×</button>
                        </div>
                        <input
                          type="text"
                          placeholder="Search Column"
                          className="column-filter-search"
                          value={columnSearch}
                          onChange={(e) => {
                            debugger;
                            setColumnSearch(e.target.value);
                            setFilterTableHeaders(
                              tableHeader.filter((h) =>
                                h.name.toLowerCase().includes(e.target.value.toLowerCase())
                              )
                            );
                          }}
                        />
                        <div className="column-filter-list">
                          <label className="column-filter-label">
                            <input
                              type="checkbox"
                              checked={
                                filterTableHeaders.length > 0 &&
                                filterTableHeaders.every((h) => tempColumns.includes(h.field))
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTempColumns((prev) => [
                                    ...prev,
                                    ...filterTableHeaders
                                      .map((h) => h.field)
                                      .filter((f) => !prev.includes(f)),
                                  ]);
                                } else {
                                  setTempColumns((prev) =>
                                    prev.filter(
                                      (f) =>
                                        !filterTableHeaders
                                          .map((h) => h.field)
                                          .includes(f)
                                    )
                                  );
                                }
                              }}
                            />
                            <span className="custom-checkbox"></span>
                            <span>All</span>
                          </label>
                          {filterTableHeaders.map((h) => (
                            <label key={h.field} className="column-filter-label">
                              <input
                                type="checkbox"
                                checked={tempColumns.includes(h.field)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTempColumns((prev) => [...prev, h.field]);
                                  } else {
                                    setTempColumns((prev) =>
                                      prev.filter((f) => f !== h.field)
                                    );
                                  }
                                }}
                              />
                              <span className="custom-checkbox"></span>
                              <span>{h.name}</span>
                            </label>
                          ))}
                        </div>
                        <div className="column-filter-actions">
                          <button className="clear-btn" onClick={() => setTempColumns([])}>Clear</button>
                          <button className="apply-btn" onClick={() => {
                            setVisibleColumns(tempColumns);
                            setShowColumnFilter(false);
                          }}>Apply</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="filter-export">
                    <ExportDropdown
                      sectionId={tableID || "datatable"}
                      sectionName={tableTitle || "Data Table"}
                      sectionData={tableData}
                      sectionDataHeaders={tableHeader}
                    />
                  </div>
                  {tableDropdown && <div className="filter">{tableDropdown}</div>}
                  {tableExportDropdown && <div className="filter-export">{tableExportDropdown}</div>}
                </div>
              </div>
            </div>
            <div className="table_wrapper">
              {DataTable}
            </div>
            {/* Pagination and entries info */}
            {totalItems !== 0 && (
              <div className="grid grid_cols_4 footer">
                <div className="grid col_span_2 info_text">
                  <Wrapper>
                    {totalItems === 0 ? (
                      "Showing 0 entries"
                    ) : totalItems <= pageSize ? (
                      `Showing 1 to ${totalItems} of ${totalItems} entries`
                    ) : (
                      `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalItems)} of ${totalItems} entries`
                    )}
                  </Wrapper>
                </div>
                {totalItems > pageSize && (
                  <div className="grid col_span_2">
                    <div className="pagination grid col_span_2 flex justify_end">
                      <div className="btn_wrapper">
                        <Button
                          handleClick={() => setCurrentPage(1)}
                          className="secondary_btn"
                          disabled={currentPage === 1}
                        >
                          1
                        </Button>
                      </div>
                      <div className="btn_wrapper">
                        <Button
                          handleClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          className="secondary_btn"
                          disabled={currentPage === 1}
                        >
                          Prev
                        </Button>
                      </div>
                      {currentPage > 2 && <span style={{ padding: "0 8px" }}>...</span>}
                      {currentPage > 1 && (
                        <div className="btn_wrapper">
                          <Button
                            handleClick={() => setCurrentPage(currentPage - 1)}
                            className="secondary_btn"
                          >
                            {currentPage - 1}
                          </Button>
                        </div>
                      )}
                      <div className="btn_wrapper">
                        <Button
                          handleClick={() => setCurrentPage(currentPage)}
                          className="primary_btn"
                        >
                          {currentPage}
                        </Button>
                      </div>
                      {currentPage < totalPages && (
                        <div className="btn_wrapper">
                          <Button
                            handleClick={() => setCurrentPage(currentPage + 1)}
                            className="secondary_btn"
                          >
                            {currentPage + 1}
                          </Button>
                        </div>
                      )}
                      {currentPage < totalPages - 1 && <span style={{ padding: "0 8px" }}>...</span>}
                      <div className="btn_wrapper">
                        <Button
                          handleClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          className="secondary_btn"
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                      <div className="btn_wrapper">
                        <Button
                          handleClick={() => setCurrentPage(totalPages)}
                          className="secondary_btn"
                          disabled={currentPage === totalPages}
                        >
                          {totalPages}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Wrapper>
        </div>
      </div>
    </div>
  );
};

NestedSummaryTable.propTypes = {
  tableTitle: PropTypes.string,
  tableHeader: PropTypes.array,
  tableData: PropTypes.array,
  tableLength: PropTypes.number,
  tableDropdown: PropTypes.any,
  searchPlaceHolder: PropTypes.string,
  searchSingleColumnNumberRow: PropTypes.bool,
  searchSingleColumnNumber: PropTypes.number,
  hrefTitle: PropTypes.string,
  tableExportDropdown: PropTypes.any,
  tableID: PropTypes.string,
  tablePlatform: PropTypes.string,
  checkbox: PropTypes.bool,
  onCheckboxClickHandler: PropTypes.func,
  isDynamicFlagVisible: PropTypes.bool,
  showGrowthDegrowth: PropTypes.bool,
  showTotal: PropTypes.bool,
  isCreatableDropdown: PropTypes.bool,
  tableTitleClass: PropTypes.string,
  expandedNestedRows: PropTypes.object,
  setExpandedNestedRows: PropTypes.func,
  handleExpandNestedRow: PropTypes.func,
  nestedData: PropTypes.object,
  loadingNested: PropTypes.object,
};

export default NestedSummaryTable;